import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface State {
	hasError: boolean;
	error: Error | null;
}

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

function DefaultErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="w-full max-w-md rounded-lg border border-destructive/30 bg-background p-6 shadow-md">
				<h1 className="mb-2 text-xl font-semibold text-destructive">{t('common.errors.somethingWentWrong')}</h1>
				{import.meta.env.DEV && error && (
					<pre className="mb-4 overflow-auto rounded bg-muted p-3 text-xs text-muted-foreground">
						{error.message}
					</pre>
				)}
				<button
					onClick={onReset}
					className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
				>
					{t('common.actions.tryAgain')}
				</button>
			</div>
		</div>
	);
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
		Sentry.captureException(error, {
			contexts: { react: { componentStack: info.componentStack } },
		});
	}

	render() {
		if (this.state.hasError) {
			return (
				this.props.fallback ?? (
					<DefaultErrorFallback
						error={this.state.error}
						onReset={() => window.location.reload()}
					/>
				)
			);
		}
		return this.props.children;
	}
}
